import useUrlState from '@ahooksjs/use-url-state'
import { useRequest, useTitle } from 'ahooks'
import { Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteChildrenProps } from 'react-router-dom'

import { LockIcon, NoPermissionPage, PERMISSIONS, hasPointPagePermission } from '@/components'
import { PpFileContainer } from '@/components/files'
import { ReportContainer } from '@/components/reports/report.container'
import { Header } from '@/modules/header'
import { userInfoAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, Portfolio, ResourceUser } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { PortfolioHeader } from './portfolio-header.component'
import { PortfolioSummaryContainer } from './portfolio-summary'

const { TabPane } = Tabs

export const PortfolioDetailContainer: React.FC<RouteChildrenProps<{ portfolioId: string }>> = ({ match }) => {
  const { t } = useTranslation()
  const portfolioId = match!.params.portfolioId
  const [viewer] = useAtom(userInfoAtom)

  const reportRef = React.useRef<{ entryTime: number; name?: string; entry?: string; users?: ResourceUser[] }>({
    entryTime: +new Date(),
  })
  const [users, setUsers] = React.useState<ResourceUser[]>()

  const [state, setState] = useUrlState(
    { tab: new URLSearchParams(window.location.search).get('tab') || 'detail' },
    {
      navigateMode: 'replace',
    }
  )

  const { data: portfolio, refresh } = useRequest<Portfolio>(
    {
      url: '/api/portfolio/getPortfolioById',
      params: {
        selfId: portfolioId,
      },
    },
    {
      refreshDeps: [portfolioId],
    }
  )

  useTitle(portfolio ? portfolio.name : document.title, { restoreOnUnmount: true })

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report.name && report.entry && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_portfolio_detail, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: report.entry,
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_portfolios_detail,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === viewer.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    const entry = window.sessionStorage.getItem('entry')
    reportRef.current = assign(reportRef.current, { entry: entry || 'ef_pmo_portfolios_detail' })
    sessionStorage.removeItem('entry')

    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      reportExposeTime()
      window.removeEventListener('beforeunload', reportExposeTime)
    }
  }, [])

  React.useEffect(() => {
    if (portfolio) {
      reportRef.current = assign(reportRef.current, { name: portfolio.name })
    }
  }, [portfolio])

  React.useEffect(() => {
    if (users) {
      reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])

  if (!portfolio) {
    return (
      <div className='flex h-full justify-center items-center'>
        <Spin size='large' delay={300} />
      </div>
    )
  }

  return (
    <>
      <Header title={<PortfolioHeader portfolio={portfolio} refresh={refresh} />} />
      <Tabs
        destroyInactiveTabPane
        activeKey={state.tab}
        onChange={(tab) => {
          switch (tab) {
            case 'detail':
              ga('event', traceEvent.portfolioTab.events.click_switch_tab, {
                event_category: traceEvent.portfolioTab.event_categories.ef_pmo_portfolio_detail,
                action: traceEvent.portfolioTab.action,
                event_label: portfolio.name,
                target: traceEvent.portfolioTab.targets.overview,
              })
              break
            case 'report':
              ga('event', traceEvent.portfolioTab.events.click_switch_tab, {
                event_category: traceEvent.portfolioTab.event_categories.ef_pmo_portfolio_detail,
                action: traceEvent.portfolioTab.action,
                event_label: portfolio.name,
                target: traceEvent.portfolioTab.targets.weekly_reports,
              })
              break
            case 'file':
              ga('event', traceEvent.portfolioTab.events.click_switch_tab, {
                event_category: traceEvent.portfolioTab.event_categories.ef_pmo_portfolio_detail,
                action: traceEvent.portfolioTab.action,
                event_label: portfolio.name,
                target: traceEvent.portfolioTab.targets.files,
              })
              break
          }
          const keys = Object.keys(state)
          setState({
            ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
            tab,
          })
          if (tab === 'detail') {
            refresh()
          }
        }}
      >
        <TabPane
          tab={
            <span>
              {t('overview')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, portfolio.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='detail'
        >
          {state.tab === 'detail' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, portfolio.permissions) ? (
              <PortfolioSummaryContainer portfolio={portfolio} setUsers={setUsers} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.概况} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('reports')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, portfolio.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='report'
        >
          {state.tab === 'report' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, portfolio.permissions) ? (
              <ReportContainer
                selfId={portfolioId}
                type={portfolio.type}
                permissions={portfolio.permissions}
                name={portfolio.name}
                isFull={false}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.周报} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('file')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, portfolio.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='file'
        >
          {state.tab === 'file' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, portfolio.permissions) ? (
              <PpFileContainer pp={portfolio} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.文件} />
            ))}
        </TabPane>
      </Tabs>
    </>
  )
}
